<template>
    <LoaderComponent v-if="isAcquiringToken" />

    <div v-else class="select-site-container">
        <div class="recherche-container">
            <span class="title">{{ title }}</span>
            <div class="first-container">
                <SearchBar :unpaged="isMultiSite" :inclure-termines="inclureTermines" class="search-bar" @filter-change="filterChange" />
                <BorderedIconButton
                    v-if="exportContact"
                    :imgUrl="iconeXls"
                    :title="$t('list.exportContacts')"
                    class="export-button"
                    @click="exportXlsContact"
                />
            </div>
        </div>
        <div class="select-all-container">
            <span class="subtitle">{{ $t('siteSelection.title.site') }}</span>
            <ReglePastille v-if="regleCle" :cle="regleCle" />
            <ButtonBlue
                v-if="isMultiSite"
                :disabled="selectedSites?.length === sites.length"
                :on-click="selectionnerTout"
                :text="$t('siteSelection.selectionner.tout')"
                class="button-selection btn"
            />
            <ButtonBlue
                v-if="isMultiSite"
                :disabled="selectedSites?.length === 0"
                :on-click="deselectionnerTout"
                :text="$t('siteSelection.deselectionner.tout')"
                class="button-selection btn"
            />
        </div>
        <div ref="siteList" class="list-container" @scroll="handleScroll">
            <SitesList
                :is-loading="isLoading"
                :is-multi-site="isMultiSite"
                :selected-sites="selectedSites"
                :selectedSite="selectedSite"
                :sites="sites"
                :sites-disabled="sitesDisabled"
                class="list"
                @add="add"
                @pick="pick"
            />
        </div>
        <WaitingModal v-if="isExportXlsLoading" />
    </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, PropType, ref } from 'vue';
import { Site } from '@/models/Site';
import SearchBar from '@/modules/SiteList/components/SearchBar.vue';
import SitesList from '@/modules/SiteList/components/SitesList.vue';
import { SiteListActions } from '../store/action-types';
import { SITE_LIST } from '../store/mutation-types';
import { SiteSearchDTO } from '@/modules/SiteList/service/SiteListService';
import BorderedIconButton from '@/components/ui-components/BorderedIconButton.vue';
import { ContactActions } from '@/modules/Contact/store/action-types';
import { store } from '@/plugins/store';
import WaitingModal from '@/components/ui-components/WaitingModal.vue';
import ButtonBlue from '@/components/ui-components/ButtonBlue.vue';
import LoaderComponent from '@/components/ui-components/LoaderComponent.vue';
import ReglePastille from '@/components/regles/ReglePastille.vue';

export default defineComponent({
    name: 'SelectSite',
    setup() {
        const siteList = ref<Element>();

        onMounted(() => {
            siteList.value;
        });

        return {
            siteList,
        };
    },
    mounted() {
        if (this.societeId != null) {
            store.dispatch('siteList/loadSitesFiltresParSociete', this.societeId);
        } else {
            if (this.sites.length === 0 && !this.isMultiSite) {
                this.$store.dispatch(`siteList/${SiteListActions.GET_SITE_LIST}`, new SiteSearchDTO(0, this.filter, false, this.inclureTermines));
            } else if (this.isMultiSite) {
                this.$store.dispatch(
                    `siteList/${SiteListActions.GET_SITE_LIST}`,
                    new SiteSearchDTO(0, this.filter, true, this.inclureTermines)
                );
            }
        }
        this.siteList?.scrollTo({ top: this.scroll });
    },
    props: {
        title: { type: String, required: true, default: '' },
        selectedSite: { type: Object as PropType<Site> },
        selectedSites: { type: Array as PropType<Site[]> },
        sitesDisabled: { type: Boolean, required: false },
        exportContact: { type: Boolean, required: false },
        isMultiSite: { type: Boolean, required: false },
        societeId: { type: Number, required: false },
        inclureTermines: { type: Boolean, required: false, default: false },
        sitesAutorises: { type: Array as PropType<Site[]>, required: false, default: () => [] },
        regleCle: { type: String, required: false },
    },
    components: {
        LoaderComponent,
        ButtonBlue,
        SearchBar,
        SitesList,
        BorderedIconButton,
        WaitingModal,
        ReglePastille,
    },
    computed: {
        isAcquiringToken(): boolean {
            return this.$store.getters['authenticationState/isAcquiringToken'];
        },
        filter(): string {
            return this.$store.getters['siteList/getFilter'];
        },
        page(): number {
            return this.$store.getters['siteList/getCurrentPage'];
        },
        numberOfPages(): number {
            return this.$store.getters['siteList/getNumberOfPages'];
        },
        sites(): Site[] {
            if (this.sitesAutorises?.length) {
                const recherche = (this.filter || '').toLowerCase();
                return (this.sitesAutorises as Site[]).filter(
                    (s: Site) => !recherche || (s.description ?? '').toLowerCase().includes(recherche)
                );
            }
            return this.$store.getters['siteList/getListValue'];
        },
        scroll(): number {
            return this.$store.getters['siteList/getScroll'];
        },
        isLoading(): boolean {
            return this.$store.getters['siteList/getIsLoading'];
        },
        iconeXls() {
            return require('@/assets/img/xls.svg');
        },
        isExportXlsLoading(): boolean {
            return this.$store.getters['contactState/isExportLoading'];
        },
    },
    methods: {
        handleScroll() {
            const height = this.siteList?.clientHeight ?? 0;
            const top = this.siteList?.scrollTop ?? 0;
            const max = this.siteList?.scrollHeight ?? 0;
            if (max - top <= height * 1.5 && this.page + 1 < this.numberOfPages && !this.isLoading) {
                this.$store.dispatch(
                    `siteList/${SiteListActions.GET_SITE_LIST}`,
                    new SiteSearchDTO(this.page + 1, this.filter, false, this.inclureTermines)
                );
            }
        },
        pick(site: Site): void {
            this.$store.commit(`siteList/${SITE_LIST.SET_SCROLL}`, site ? this.siteList?.scrollTop : 0);
            this.$store.commit(`siteList/${SITE_LIST.SET_SITE_ID}`, site ? site.id : 0);
            this.$emit('pick', site);
        },
        add(sites: Site[]): void {
            this.$store.commit(`siteList/${SITE_LIST.SET_SCROLL}`, sites ? this.siteList?.scrollTop : 0);
            this.$emit('add', sites);
        },
        filterChange() {
            this.$emit('filter-change');
            // mantis 47007 : bug filtre sites via searchBar (par défaut tout sélectionner / searchBar ne déselectionne pas la liste malgré affichage filtré)
            // => tout désélectionner quand on utilise la searchBar
            this.$emit('add', []);
        },
        exportXlsContact(): void {
            store.dispatch(`contactState/${ContactActions.GET_EXPORT_CONTACTS}`, {
                recherche: this.$store.getters['siteList/getFilter'],
                siteId:
                    this.$store.getters['siteList/getSiteId'] === 0
                        ? undefined
                        : this.$store.getters['siteList/getSiteId'],
            });
        },
        selectionnerTout(): void {
            if (!this.sitesDisabled) {
                if (this.selectedSites?.length !== this.sites.length) {
                    this.$emit('addAll', this.sites);
                } else {
                    this.$emit('add', []);
                }
            }
        },
        deselectionnerTout(): void {
            if (!this.sitesDisabled) {
                this.$emit('add', []);
            }
        },
    },
});
</script>

<style scoped>
.select-site-container {
    display: flex;
    flex-direction: column;
    flex: 1;
    padding: 16px 24px 12px 24px;
}
.search-bar {
    margin: 8px 0 16px 0;
    width: 33%;
}
.title {
    color: var(--dark-blue);
    font-weight: var(--font-weight-medium);
    font-size: var(--font-size-medium);
}
.subtitle {
    color: var(--dark-grey);
}
.list-container {
    margin-top: 8px;
    overflow: auto;
}
.first-container {
    display: flex;
}
.export-button {
    margin-left: auto;
    margin-right: 0;
    height: 38px;
}
.button-selection {
    right: 16px;
}
.btn {
    width: 120px;
    border-radius: 5px;
    cursor: pointer;
    height: 40px;
    margin: 5px;
}
</style>
